/**
 * Normalize a value's derivation chain into ONE render tree, from either source:
 *
 *  - LOCAL: the delivered `CitedValue` already carries its inputs
 *    (`DerivedSourceRef.inputs`, recursive) — a per-turn derivation tree that
 *    renders with zero network calls (works with today's payloads).
 *  - REMOTE: when the value carries an `atom_id`, the lineage endpoint returns
 *    the durable cross-run DAG (`evidence_atom_edges`); we fold its typed edges
 *    into the same tree so the UI renders `evidence_atom_edges` directly (§17).
 *
 * Both collapse to a `LineageNode` tree the view renders recursively, and the
 * remote one is MERGED INTO the local one rather than replacing it
 * (:func:`mergeLineage`). Replacing was a real bug: the durable DAG carries
 * locators and no display values, so hydration blanked the figures the local
 * payload had already put on screen — a card that loaded and then told the
 * reader less than it had a moment earlier.
 */

import type { CitedValue, SourceRef } from "../../types/citationArtifact";
import type {
	EdgeRelation,
	EvidenceAtom,
	EvidenceLineage,
} from "../../types/evidenceLineage";
import { describeSource } from "./sourceKind";

export interface LineageNode {
	id: string;
	title: string;
	/** The clickable source ref for this node (absent for the synthetic root). */
	ref?: SourceRef;
	/** How this node relates to its parent (for the connector label). */
	relation?: EdgeRelation | "input";
	children: LineageNode[];
}

/**
 * How a node identifies its source, for matching local against remote.
 *
 * Structural, because the two sides arrive from different producers and share
 * no node ids: the local tree is built from the delivered `CitedValue` and the
 * remote one from `evidence_atoms`. Two nodes with the same identity are the
 * same source seen twice, and must render once.
 */
export function refIdentity(ref: SourceRef | undefined): string | null {
	if (!ref) return null;
	switch (ref.kind) {
		case "excel":
			return `excel:${ref.file_id}:${(ref.sheet ?? "").toLowerCase()}:${(
				ref.cell ??
				ref.range ??
				""
			)
				.replace(/\$/g, "")
				.toLowerCase()}`;
		case "pdf":
			return `pdf:${ref.file_id}:${ref.page ?? ""}`;
		case "web":
			return `web:${ref.url}`;
		case "record":
			return `record:${ref.source_system}:${ref.object_type}:${ref.record_id}:${ref.field}`;
		case "comms":
			return `comms:${ref.source_system}:${ref.message_id}`;
		case "derived":
			return `derived:${ref.operation}:${ref.expression ?? ref.formula ?? ""}`;
		default:
			return null;
	}
}

/**
 * The value a cited cell holds, when the payload carries it.
 *
 * The backend populates `display_value` / `raw_value` / `label` on an
 * `ExcelSourceRef` for exactly this — its schema comment says the point is to
 * render a derived value's inputs as "<metric> = <value>" rather than a bare
 * "Sheet!B63" — and the frontend had never read them, so every input chip in a
 * derivation showed a locator and no figure. A reader checking an arithmetic
 * result could see which cells were added but not what was in them.
 *
 * The locator stays in front: it is the auditable half, and the chip's tooltip
 * (built by `describeSource`) still names the file.
 */
function valueSuffix(ref: SourceRef): string {
	if (ref.kind !== "excel") return "";
	const shown = ref.display_value ?? ref.raw_value;
	if (shown == null || String(shown).trim() === "") return "";
	return ` = ${String(shown)}`;
}

// --- Local: build from the CitedValue's own source tree ---------------------

let localCounter = 0;

function sourceToNode(source: SourceRef, relation: LineageNode["relation"]): LineageNode {
	const desc = describeSource(source);
	const node: LineageNode = {
		id: `local-${(localCounter += 1)}`,
		title: `${desc.chipLabel}${valueSuffix(source)}`,
		ref: source,
		relation,
		children: [],
	};
	if (source.kind === "derived") {
		node.children = (source.inputs ?? []).map((inp) => sourceToNode(inp, "input"));
	}
	return node;
}

export function buildLocalLineage(value: CitedValue): LineageNode {
	localCounter = 0;
	return {
		id: value.value_id ?? "root",
		title: value.metric_label
			? `${value.metric_label}: ${value.display}`
			: value.display,
		children: (value.sources ?? []).map((s) => sourceToNode(s, "input")),
	};
}

/** True when the value has at least one derivation input worth expanding. */
export function hasLocalLineage(value: CitedValue): boolean {
	return (value.sources ?? []).some(
		(s) => s.kind === "derived" && (s.inputs?.length ?? 0) > 0,
	);
}

// --- Remote: fold the durable atom DAG into the same tree -------------------

function atomTitle(atom: EvidenceAtom): string {
	return describeSource(atom.source_ref).chipLabel;
}

/**
 * Build a downward "value ← inputs" tree from the durable DAG. Edge directions
 * are normalized so every relation points from a node to the input beneath it:
 *   - derived_from: parent(value) → child(input)      [keep]
 *   - input_to:     parent(input) → child(value)      [invert]
 *   - supersedes:   parent(new)   → child(superseded) [keep, labelled]
 */
export function buildRemoteLineage(lineage: EvidenceLineage): LineageNode | null {
	const byId = new Map<string, EvidenceAtom>();
	for (const node of lineage.nodes) byId.set(node.atom_id, node);
	const root = byId.get(lineage.root_atom_id);
	if (!root) return null;

	const children = new Map<string, { child: string; relation: EdgeRelation }[]>();
	const push = (parent: string, child: string, relation: EdgeRelation) => {
		const list = children.get(parent) ?? [];
		list.push({ child, relation });
		children.set(parent, list);
	};
	for (const edge of lineage.edges) {
		if (edge.relation === "input_to") {
			push(edge.child_atom_id, edge.parent_atom_id, edge.relation);
		} else {
			push(edge.parent_atom_id, edge.child_atom_id, edge.relation);
		}
	}

	const build = (atomId: string, relation: LineageNode["relation"], seen: Set<string>): LineageNode => {
		const atom = byId.get(atomId);
		const kids = children.get(atomId) ?? [];
		return {
			id: atomId,
			title: atom ? atomTitle(atom) : atomId,
			ref: atom?.source_ref,
			relation,
			children: kids
				.filter((k) => !seen.has(k.child))
				.map((k) => build(k.child, k.relation, new Set([...seen, k.child]))),
		};
	};

	return build(lineage.root_atom_id, undefined, new Set([lineage.root_atom_id]));
}

// --- Merge: hydration must only ever ADD --------------------------------

/**
 * Fold the durable DAG into the tree already on screen.
 *
 * The rule is one-directional: hydration may add nodes, never remove or blank
 * one. That is not a stylistic preference — it is the bug this function exists
 * to prevent. `LineageView` used to swap `local` for `remote` wholesale on
 * fetch resolve, and because the durable atoms carry locators with no display
 * values, the swap replaced "EBITDA: 58,000" with "Computed · arithmetic" and
 * dropped every input's figure. A card that has already told the reader
 * something must not take it back a second later; the reader has no way to
 * know which of the two states to believe.
 *
 * So:
 *
 * - **The local root wins.** Both roots are the same value, and the local one
 *   names it ("EBITDA: 58,000") while the durable one can only describe its
 *   locator.
 * - **A node present on both sides keeps its local title** (values included)
 *   and gains any remote-only children beneath it — which is what the durable
 *   DAG is actually for: ancestry from other runs that this turn's payload
 *   never had.
 * - **Remote-only nodes are appended** after the local ones, so the order the
 *   reader first saw is stable.
 */
export function mergeLineage(
	local: LineageNode,
	remote: LineageNode | null,
): LineageNode {
	if (!remote) return local;

	// The two trees are shaped differently and have to be lined up before they
	// can be merged. The local tree wraps its inputs in a `derived` node under
	// the value's root (root -> "Computed · arithmetic" -> cells), while the
	// durable DAG's ROOT IS that derived atom, with the cells directly beneath
	// it. Attaching the remote children to the local root would file each cell
	// one level too high and — because the same cell also sits under the
	// derived node — show it twice.
	const derivedChild = local.children.find((c) => c.ref?.kind === "derived");
	const seen = new Set<string>();
	collectIdentities(local, seen);

	if (derivedChild) {
		return {
			...local,
			children: local.children.map((child) =>
				child === derivedChild ? mergeNode(child, remote, seen) : child,
			),
		};
	}
	return mergeNode(local, remote, seen);
}

/** Every source already on screen, so hydration cannot re-add one. */
function collectIdentities(node: LineageNode, into: Set<string>): void {
	const identity = refIdentity(node.ref);
	if (identity) into.add(identity);
	for (const child of node.children) collectIdentities(child, into);
}

function mergeNode(
	local: LineageNode,
	remote: LineageNode,
	seen: Set<string>,
): LineageNode {
	const remoteByIdentity = new Map<string, LineageNode>();
	for (const child of remote.children) {
		const identity = refIdentity(child.ref);
		if (identity) remoteByIdentity.set(identity, child);
	}

	const merged: LineageNode[] = [];
	const consumed = new Set<string>();
	for (const child of local.children) {
		const identity = refIdentity(child.ref);
		const match = identity ? remoteByIdentity.get(identity) : undefined;
		if (match) {
			consumed.add(identity as string);
			merged.push(mergeNode(child, match, seen));
		} else {
			merged.push(child);
		}
	}
	for (const child of remote.children) {
		const identity = refIdentity(child.ref);
		// Skip anything already rendered anywhere in the local tree, not just
		// among this node's own children: the same cell can legitimately appear
		// at a different depth on the two sides, and showing it twice would
		// read as two inputs where there is one.
		if (identity && (consumed.has(identity) || seen.has(identity))) continue;
		if (identity) seen.add(identity);
		merged.push(child);
	}

	// Local title and local ref throughout: the remote node describes the same
	// source with strictly less to say about it.
	return { ...local, children: merged };
}
